import { useEffect, useState, useMemo, useReducer } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U2103ROLES } from "../U2103";
import U2103UserBody from "./form/u2103User/U2103UserBody";
import { U2103UserFormToolBar } from "./form/u2103User/U2103UserFormToolBar";
import U2103UserDeleteDlg from "./common/U2103UserDeleteDlg";
import { useU2103Context } from "../U2103Context";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000IsValidEmail } from "../../u2000Core/tools/U2000EmailHelper";
import U2103UserShowSecretKeyDlg from "./form/u2103User/U2103UserShowSecretKeyDlg";
import { useU2103GetSecretKeyHandlers } from "./form/u2103User/U2103UseGetSecretKeyHandlers";
import U2103UserActionsBody from "./form/u2103User/u2103UerActionsTab/U2103UserActionBody";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { doCreateData, doDeleteData, doGetAuditsData, doGetData, doGetSecretKeyData, doSearch, doUpdateData, doUpdatePwdData, emptyUser, luHandleAppiError, u2103PrepareUserSavePayload, type U2103User } from "../U2103Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U2103UserRolesBody from "./form/u2103User/u2103UserRolesTab/U2103UserRoleBody";
import U2103UserOrganizationsBody from "./form/u2103User/u2103UserOrganizationsTab/U2103UserOrganizationsBody";
import { U2000GeneratePassword } from "../../u2000Core/tools/U2000StringUtils";
import { generateTemporaryId } from "../../u2000Core/tools/U2000IdUtils";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2103UserFormExtendedHandlers extends U2000FormStateHandlersReturn<U2103User> {
  addNewUser: () => void;
  modifyUser: (role: U2103User) => void;
  deleteUser: (id: string) => Promise<void>;
  resetPassword: () => void;
  showSecretKey: () => void;
}

const U2103UserForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u2101LogicalUnitBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // manage show secret key dialog state
  const [isDialogSecretKeyOpen, setIsDialogSecretKeyOpen] = useState(false);

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2103Context();

  // Form Handlers and default methods
  const validateForm = (user: U2103User) => {
    const result: Record<string, string> = {};
    if (!user.username || user.username.trim() === "") {
      result["username"] = t("errUserNameRequired", { ns: NS });
    }
    if (!user.firstName || user.firstName.trim() === "") {
      result["firstName"] = t("errFirstNameRequired", { ns: NS });
    }
    if (!user.lastName || user.lastName.trim() === "") {
      result["lastName"] = t("errLastNameRequired", { ns: NS });
    }
    if (!user.email || user.email.trim() === "") {
      result["email"] = t("errEmailRequired", { ns: NS });
    }
    if (user.email && !u2000IsValidEmail(user.email)) {
      result.email = t("errorB0010", { ns: NS });
    }

    const oldUser = u2103UserFormStateExtendedHandlers.getOldData() as U2103User | null;
    const disablingSso = Boolean(oldUser?.ssoEnabled) && !user.ssoEnabled;
    const passwordRequiredForAdd =
      u2103UserFormStateExtendedHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD && !user.ssoEnabled;
    const passwordRequiredForReset = "resetPwd" in u2103UserFormStateExtendedHandlers.getCustomAttributes();

    if (
      (passwordRequiredForAdd || passwordRequiredForReset || disablingSso) &&
      (typeof user.password !== "string" || user.password.trim() === "")
    ) {
      result.password = disablingSso
        ? t("errorBU210304", { ns: NS })
        : t("errPasswordRequired", { ns: NS });
    }
    return result;
  };

  const u2103UserBL: U2000FormSBusinessHandlers<U2103User> = {
    newRecord: () => {
      const user = { ...emptyUser };
      user.id = generateTemporaryId();
      user.password = U2000GeneratePassword();
      user.roles = [];
      user.actions = [];
      return user;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (user: U2103User, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(user);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(user, editMode);
      } else {
        u2103UserFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const user = u2103UserFormStateExtendedHandlers.getOldData() as U2103User | null;
      if (user) {
        // Met à false checkedId pour chaque action
        if ("actions" in user && Array.isArray((user as any).actions)) {
          (user as any).actions = (user as any).actions.map((action: any) => ({ ...action, idChecked: false }));
        }
        if ("roles" in user && Array.isArray((user as any).roles)) {
          (user as any).roles = (user as any).roles.map((role: any) => ({ ...role, idChecked: false }));
        }
        u2103UserFormStateExtendedHandlers.setData({ ...user });
      }
      u2103UserFormStateExtendedHandlers.setCustomAttributes({});

      if (luStateHandlers.getActionOriginFrom() === "/U2103") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (user) => {
      if (user.id) {
        handleGet.getData(user.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U2103");
    },
  };

  const u2103UserFormStateHandlers = useU2000FormStateHandlers<U2103User>({
    formState,
    formStateDispatch,
    businessHandlers: u2103UserBL,
  });

  // extended methods
  const u2103UserFormStateExtendedHandlers: U2103UserFormExtendedHandlers = {
    ...u2103UserFormStateHandlers,

    addNewUser: () => {
      u2103UserFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyUser: () => {
      u2103UserFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteUser: async (_id: string) => {
      handleDelete.handleDelete();
    },
    resetPassword: () => {
      const user = u2103UserFormStateExtendedHandlers.getData();
      user.password = U2000GeneratePassword();
      u2103UserFormStateExtendedHandlers.setData(user);
      u2103UserFormStateExtendedHandlers.setCustomAttributes({ ...u2103UserFormStateExtendedHandlers.getCustomAttributes(), resetPwd: true });
      u2103UserFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    showSecretKey: () => {
      handleGetSecretKeyHandlers.GetSecretKey();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U2103User>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U2103User>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U2103User & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (logicalUnit: U2103User & { id: string }) => {
      gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      u2103UserFormStateExtendedHandlers.setData(logicalUnit);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U2103", label: t("title", { ns: NS }) }, { label: logicalUnit.firstName + " " + logicalUnit.lastName + " (" + logicalUnit.username + ")" }]);

      // Set breadcrumb for this user
      try {
        const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
        setLogicalUnitBreadcrumb([
          { href: "/U2103", label: t("title", { ns: NS }) },
          { label: (logicalUnit.firstName || "") + " " + (logicalUnit.lastName || "") + (logicalUnit.username ? " (" + logicalUnit.username + ")" : "") },
        ]);
      } catch (e) {
        // noop if context not available
      }

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2103UserFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U2103User>({
    doSaveFn: (user: U2103User, editMode: U2000FormStateEnumEditMode) => {
      const oldUser = u2103UserFormStateExtendedHandlers.getOldData() as U2103User | null;
      const payload = u2103PrepareUserSavePayload(user, oldUser?.ssoEnabled);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, payload);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        if ("resetPwd" in u2103UserFormStateExtendedHandlers.getCustomAttributes()) {
          return doUpdatePwdData(u2000AxiosAuthenticated, payload.id, payload);
        }
        return doUpdateData(u2000AxiosAuthenticated, payload.id, payload);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      else return Promise.resolve(undefined);
    },
    onSuccess: (logicalUnit: U2103User & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      u2103UserFormStateExtendedHandlers.setCustomAttributes({});
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), logicalUnit]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      }
      u2103UserFormStateExtendedHandlers.afterSaveData(logicalUnit);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2103") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2103UserFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2103UserFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2103UserFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["username"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U2103", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const handleGetSecretKeyHandlers = useU2103GetSecretKeyHandlers({
    doGetSecretKeyFn: () => {
      // doLoadLogicalUnitActions expects one argument; provide a typed dummy value to satisfy the signature.
      return doGetSecretKeyData(u2000AxiosAuthenticated, u2103UserFormStateExtendedHandlers.getData()?.id);
    },
    onSuccess: (user: U2103User) => {
      if (user) {
        u2103UserFormStateExtendedHandlers.setData({ ...u2103UserFormStateExtendedHandlers.getData(), secretKey: user.secretKey });
        setIsDialogSecretKeyOpen(true);
      }
    },
    onError: ({ error }) => {
      // Gérer les erreurs lors du chargement des actions d'une unité logique
      console.error("Error loading logical unit actions:", error);
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U2103/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u2103UserFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u2103UserFormStateExtendedHandlers.setData({ ...emptyUser });
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabRole",
      label: t("tabRoles", { ns: NS }),
      content: <U2103UserRolesBody u2103UserFormHandlers={u2103UserFormStateExtendedHandlers} />,
    },
    {
      id: "tabAction",
      label: t("tabActions", { ns: NS }),
      content: <U2103UserActionsBody u2103UserFormHandlers={u2103UserFormStateExtendedHandlers} />,
    },
    {
      id: "tabOrganizations",
      label: t("tabOrganizations", { ns: NS }),
      content: <U2103UserOrganizationsBody u2103UserFormHandlers={u2103UserFormStateExtendedHandlers} />,
    },
    hasRole(U2103ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2103UserFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U2103UserAudit" />,
    },
  ].filter(Boolean);

  // import pour eciter les boocles infinies
  const formEditMode = useMemo(() => u2103UserFormStateExtendedHandlers.getFormEditMode(), [u2103UserFormStateExtendedHandlers]);

  // permet de mettre à jour les donnees apres le changement du mode edit (ADD ou EDIT)
  useEffect(() => {
    if (formEditMode === U2000FormStateEnumEditMode.EDIT) {
      if ("resetPwd" in u2103UserFormStateExtendedHandlers.getCustomAttributes()) {
        const user = u2103UserFormStateExtendedHandlers.getData();
        if (user.locked) {
          user.locked = false;
        }
        user.changePasswordOnLogin = true;
        u2103UserFormStateExtendedHandlers.setData(user);
      }
    }
  }, [formEditMode]);

  if (u2103UserFormStateExtendedHandlers.getData() === null) {
    return null;
  }

  // Render
  return (
    <>
      <U2103UserDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2103UserShowSecretKeyDlg
        isDialogSecretKeyOpen={isDialogSecretKeyOpen}
        secretKey={u2103UserFormStateExtendedHandlers.getData()?.secretKey}
        onClose={() => setIsDialogSecretKeyOpen(false)}
        t={t}
        NS={NS}
      />

      <U2000DefaultForm
        actionsBtns={<U2103UserFormToolBar formStateHandlers={u2103UserFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u2103UserFormStateExtendedHandlers as any}
      >
        <U2103UserBody formStateHandlers={u2103UserFormStateExtendedHandlers as any} t={t} />

        <U2000Tab className="mt-4" tabs={formTabsComponent} defaultTab="tabRole" activeTab={activeTab ?? "tabRole"} setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))} />
      </U2000DefaultForm>
    </>
  );
};

export default U2103UserForm;
